<template>
  <div
      class="input-box"
      @click="focus"
  >
    <textarea
        ref="textareaRef"
        class="chat-textarea"
        :value="modelValue"
        :disabled="disabled"
        placeholder="给 小安 发送消息... (Enter 发送，Shift+Enter 换行)"
        rows="1"
        @input="onInput"
        @keydown="onKeydown"
    />
    <div class="input-footer">
      <div class="input-footer-left">
        <el-tooltip content="上传文件" placement="top">
          <button class="input-icon-btn" type="button" @click.stop="triggerFileSelect">
            <el-icon :size="20"><Paperclip /></el-icon>
          </button>
        </el-tooltip>
        <el-tooltip :content="isRecording ? '停止录音' : '语音输入'" placement="top">
          <button
              class="input-icon-btn"
              type="button"
              :class="{ 'recording': isRecording }"
              @click.stop="toggleRecording"
          >
            <el-icon :size="20">
              <Microphone v-if="!isRecording" />
              <VideoPause v-else />
            </el-icon>
          </button>
        </el-tooltip>
        <!-- 插槽：用于显示外部传入的文件列表（如拖拽上传） -->
        <slot name="fileList"></slot>
        <!--        <span v-if="modelName" class="model-hint">{{ modelName }}</span>-->
      </div>
      <button
          class="send-btn"
          :class="{ 'send-btn--active': modelValue.trim() && !disabled }"
          :disabled="!modelValue.trim() || disabled"
          type="button"
          @click="sendMessage"
      >
        <el-icon :size="20" v-if="!disabled"><Top /></el-icon>
        <el-icon v-else class="spin"><Top /></el-icon>
      </button>
    </div>
    <input
        ref="fileInputRef"
        type="file"
        multiple
        style="display: none"
        @change="handleFileSelect"
    />
  </div>
</template>

<script setup lang="ts">
import {onBeforeUnmount, ref, watch} from 'vue'
import { ElMessage } from 'element-plus'
import type { UploadedFile } from '@/types'
import { Close, Microphone, VideoPause } from '@element-plus/icons-vue'
import WebSocketRecorder from '@/utils/websocketRecorder'

const props = defineProps<{
  modelValue: string
  disabled?: boolean
  modelName?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  send: []
  keydown: [e: KeyboardEvent]
  fileUpload: [files: UploadedFile[]]
  removeFile: [fileId: string]
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const isRecording = ref(false)
const wsRecorder = ref<WebSocketRecorder | null>(null)

function onInput(e: Event) {
  const el = e.target as HTMLTextAreaElement
  emit('update:modelValue', el.value)
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 160) + 'px'
}

function onKeydown(e: KeyboardEvent) {
  emit('keydown', e)
  if (wsRecorder.value) {
    wsRecorder.value.stopRecording()
    isRecording.value = false
  }
}

function triggerFileSelect() {
  fileInputRef.value?.click()
}

function handleFileSelect(e: Event) {
  const input = e.target as HTMLInputElement
  const files = input.files
  if (files && files.length > 0) {
    processFiles(files)
  }
  input.value = ''
}

function processFiles(fileList: FileList) {
  const newFiles: UploadedFile[] = []
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i]
    const uploadedFile: UploadedFile = {
      id: `file_${Date.now()}_${i}`,
      file: file,
      uploading: false,
      progress: 0
    }
    newFiles.push(uploadedFile)
  }
  emit('fileUpload', newFiles)
  ElMessage.success(`已添加 ${newFiles.length} 个文件`)
}

function removeFile(fileId: string) {
  // 通知父组件删除文件
  emit('removeFile', fileId)
}

function focus() {
  textareaRef.value?.focus()
}
function sendMessage() {
  emit('send')
  if (wsRecorder.value) {
    wsRecorder.value.stopRecording()
    isRecording.value = false
  }
}
async function toggleRecording() {
  if (isRecording.value) {
    // 停止录音
    if (wsRecorder.value) {
      wsRecorder.value.stopRecording()
      isRecording.value = false
    }
  } else {
    // 开始录音
    try {
      if (!wsRecorder.value) {
        wsRecorder.value = new WebSocketRecorder()
      }

      let lastFinalText = ''

      // 设置识别结果回调
      wsRecorder.value.onTranscript = (msg) => {
        console.log('🎤 识别结果:', msg)
        if (msg.type === 'partial') {
          // 部分识别结果：替换当前文本（不累积）
          const transcript = msg.text || ''
          if (transcript) {
            emit('update:modelValue', transcript)
          }
        } else if (msg.type === 'final') {
          // 最终识别结果：只有在文本变化时才更新
          const transcript = msg.text || ''
          if (transcript && transcript !== lastFinalText) {
            lastFinalText = transcript
            emit('update:modelValue', transcript)
            // ElMessage.success('识别完成')
          }
        }
      }

      const result = await wsRecorder.value.startRecording()
      if (result === 'success') {
        isRecording.value = true
        ElMessage.info('正在录音，请说话...')
      } else if (result === 'viocefail') {
        ElMessage.error('录音启动失败，请检查麦克风权限和网络连接')
      }
    } catch (error) {
      console.error('录音启动错误:', error)
      ElMessage.error('无法启动录音，请检查麦克风权限')
    }
  }
}

onBeforeUnmount(() => {
  if (wsRecorder.value) {
    wsRecorder.value.stopRecording()
    wsRecorder.value.closeWS()
    wsRecorder.value = null
  }
})

defineExpose({ focus })
</script>

<style scoped>
.input-box {
  border: 1.5px solid rgba(57, 124, 253, 0.22);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 24px rgba(78, 110, 242, 0.16);
  transition: border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
  position: relative;
  cursor: text;
}

.input-box:focus-within {
  border-color: #397CFD;
  box-shadow: 0 4px 24px rgba(78, 110, 242, 0.16);
}

.chat-textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  font-size: 15px;
  line-height: 1.6;
  color: #1a1a1e;
  padding: 14px 16px 6px;
  font-family: inherit;
  background: transparent;
  min-height: 52px;
  max-height: 160px;
  display: block;
}
.chat-textarea::placeholder { color: #bbb; }
.chat-textarea:disabled { opacity: 0.7; cursor: not-allowed; }

.input-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 10px 12px;
  position: relative;
  z-index: 1;
}
.input-footer-left {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
  max-width: 70%;
}
.input-icon-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #707688;
  border-radius: 7px;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.15s;
}
.input-icon-btn:hover { background: #f0f0f0; color: #666; }

.file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: 8px;
}
.file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0f3ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  font-size: 12px;
}
.file-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #4e6ef2;
}
.remove-file {
  cursor: pointer;
  color: #999;
  transition: color 0.15s;
}
.remove-file:hover {
  color: #f56c6c;
}

.model-hint {
  font-size: 11px;
  color: #ccc;
  margin-left: 6px;
  user-select: none;
}

.send-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(57, 124, 253, 0.30);
  color: rgba(255, 255, 255, 1);
  border-radius: 50%;
  cursor: not-allowed;
  font-size: 17px;
  transition: all 0.2s;
  flex-shrink: 0;
}
.send-btn--active {
  background: linear-gradient(135deg, #667eea, #4e6ef2);
  color: #fff;
  cursor: pointer;
}
.send-btn--active:hover {
  background: linear-gradient(135deg, #764ba2, #4e6ef2);
  transform: scale(1.05);
}

.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
